import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from 'react'
import { Loader2, ShieldCheck, Zap, FolderPlus, Check, X, ChevronLeft } from 'lucide-react'
import { miniappApi, type MiniAppConsentItem } from '@papan/files/client'
import { ConsentItemLabel } from './ConsentItemLabel.js'
import { useMiniappConsentSignal } from './useMiniappConsentSignal.js'
import { AppKeyPicks, AppKeysStepIntro, AppKeyPickSummary, type ChosenKeyView } from '@papan/keybook/ui/app-key-picks'
import { errorText, pinnedBatch } from '@papan/api-client'

/**
 * MiniAppInstallGate — the stepped approval flow in front of the RUNNER
 * (docs/plans/2026-07-14_(completed)_MINIAPP_INSTALL_WIZARD.md, Phase 2).
 *
 * Numbered steps with a Back button; item decisions are COLLECTED and committed
 * once at the end:
 *   Step 1  — Install (read+write to the app's OWN folder). Required.
 *   Automations — the events the app reacts to, as a checklist. We describe the
 *     EVENT ("a file is created in /reports/"), not the code: the code is opaque
 *     and any description could lie, and what it can DO is already bounded by the
 *     grants below. Check what to allow; none checked → Allow all / Deny all.
 *   Folder access — the `.chain`s the app wants to reach, same checklist shape.
 *   Keys — the app's secrets this person can't simply allow (no key named, a key they can't open, no field): each
 *     gets a picker of THEIR keys, saved at once as the app's variable (keyId/field + the grant) — before Confirm,
 *     with exactly the right Variables and secrets uses (so it stays saved if the flow is abandoned). Any left
 *     unchosen are set aside at Confirm, like an unticked item — Variables and secrets still lists them.
 *
 * Install-state is only readable by a caller who passes the modification safeguard;
 * when we can't read it (viewer 403 / transient) we FAIL OPEN to running.
 *
 * LIVE: the gate re-checks whenever the consent state may have changed (an AI or
 * Settings write to triggers.json, a new `.chain`, a permission change naming the
 * app) — not only on mount. Once the app has run, a newly outstanding item opens
 * the flow as an OVERLAY above the still-mounted app, so answering it neither
 * restarts the app nor drops its state.
 */
type StepKind = 'install' | 'triggers' | 'grants' | 'keys' | 'confirm'
const STEP_ORDER: StepKind[] = ['install', 'triggers', 'grants', 'keys', 'confirm']
function stepsFor(needsInstall: boolean, triggerCount: number, grantCount: number, pickCount = 0): StepKind[] {
  const s: StepKind[] = []
  if (needsInstall) s.push('install')
  if (triggerCount) s.push('triggers')
  if (grantCount) s.push('grants')
  if (pickCount) s.push('keys')
  // A final review page whenever there are item decisions to confirm; an
  // install-only flow commits on its own button (no separate review needed).
  if (triggerCount || grantCount || pickCount) s.push('confirm')
  return s
}

// Commits still being written, per app folder — shared by every mounted gate.
// A check for a folder whose decisions are in flight read the server BEFORE the
// write landed, and would raise the answered items again with every box
// unticked. Module-level, not per gate: closing and reopening the app window
// mounts a NEW gate while the old one's save is still on the wire.
// Cost, accepted: a gate that opens mid-save shows "Loading…" until that save
// returns — bounded by the API client's per-request timeout.
const commitsInFlight = new Map<string, number>()
const commitSettledWaiters = new Map<string, Set<() => void>>()
// Bumped when a commit STARTS. A read is only trustworthy if no commit was in
// flight when it began and none began since: asking "in flight?" only when the
// response lands misses a save that ended while the read was on the wire.
const commitEpochs = new Map<string, number>()
function commitInFlight(folderId: string): boolean {
  return (commitsInFlight.get(folderId) ?? 0) > 0
}
/** Snapshot taken before a read; `stale()` says whether a commit overlapped it. */
function commitSnapshot(folderId: string): { stale: () => boolean } {
  const inFlight = commitInFlight(folderId)
  const epoch = commitEpochs.get(folderId) ?? 0
  return { stale: () => inFlight || commitInFlight(folderId) || (commitEpochs.get(folderId) ?? 0) !== epoch }
}
// Key saves from the Keys step still running, per app folder — module-level like the commits above: a visit that
// starts after a folder swap (A → B → A) must still see A's save in flight, or its Confirm would set aside the key
// being saved.
const pickSavesInFlight = new Map<string, number>()
function pickSaveDelta(folderId: string, delta: number): void {
  const n = Math.max(0, (pickSavesInFlight.get(folderId) ?? 0) + delta)
  if (n) pickSavesInFlight.set(folderId, n); else pickSavesInFlight.delete(folderId)
}

/** Test-only: a failed test must not leave a folder "in flight" for the rest of the file. */
export function resetConsentCommitTrackingForTests(): void {
  commitsInFlight.clear(); commitSettledWaiters.clear(); commitEpochs.clear(); pickSavesInFlight.clear()
}
/** Run `fn` once no commit for `folderId` is in flight any more. */
function afterCommitSettles(folderId: string, fn: () => void): void {
  let set = commitSettledWaiters.get(folderId)
  if (!set) commitSettledWaiters.set(folderId, set = new Set())
  set.add(fn)
}
/** Mark a commit for `folderId` in flight; the returned function ends it. */
function beginCommit(folderId: string): () => void {
  commitsInFlight.set(folderId, (commitsInFlight.get(folderId) ?? 0) + 1)
  commitEpochs.set(folderId, (commitEpochs.get(folderId) ?? 0) + 1)
  return () => {
    const left = (commitsInFlight.get(folderId) ?? 1) - 1
    if (left > 0) { commitsInFlight.set(folderId, left); return }
    commitsInFlight.delete(folderId)
    const waiters = commitSettledWaiters.get(folderId)
    commitSettledWaiters.delete(folderId)
    waiters?.forEach(fn => fn())
  }
}

export function MiniAppInstallGate({ folderId, folderName, children, onAppMountedChange, loadingView, onOwnUiChange }: {
  folderId: string
  folderName?: string
  children: ReactNode
  /** What to show while the gate checks the install (#538: the app window's PDF → PDApp morph). */
  loadingView?: ReactNode
  /** #538 — fires when the gate starts / stops showing its OWN screen (the install steps, "needs install")
   *  in place of the app: the window's loading morph must not cover it. */
  onOwnUiChange?: (own: boolean) => void
  /**
   * Fires whenever the app underneath goes from mounted to not, or back.
   *
   * The host needs it for Ctrl+Z. While this gate shows "Loading…" or the
   * not-approved notice, the editor inside is NOT mounted and claims no
   * keystroke — and an unclaimed Ctrl+Z leaves the tab entirely and reaches
   * the drive's file history, restoring a file the user deleted minutes ago.
   * The host takes the claim over while this reports false.
   */
  onAppMountedChange?: (mounted: boolean) => void
}) {
  const [phase, setPhase] = useState<'loading' | 'flow' | 'done' | 'needs-install'>('loading')
  const [needsInstall, setNeedsInstall] = useState(false)
  const [triggers, setTriggers] = useState<MiniAppConsentItem[]>([])
  const [grants, setGrants] = useState<MiniAppConsentItem[]>([])
  // Secrets to choose a key for (`key-pick`), and the ones chosen in this flow (saved already — kept on screen as done).
  const [picks, setPicks] = useState<MiniAppConsentItem[]>([])
  const [chosen, setChosen] = useState<Set<string>>(new Set())
  // A key being saved from the Keys step: Continue / Confirm wait for it (a set-aside sent meanwhile would race it).
  // Saves in flight are counted per FOLDER at module level (`pickSavesInFlight`): going Back and returning remounts the
  // picker, and a folder swap starts a new visit — neither may forget a save still running. This only re-renders.
  const [, setPickSaveTick] = useState(0)
  const pickSaving = pickSavesInFlight.get(folderId) ?? 0
  // Display only: how each chosen key is shown (its chip) on the Keys step and the review, per app folder + pick. What is
  // decided stays `chosen`; this only names the key a chosen row shows.
  const chosenKeyViews = useRef(new Map<string, ChosenKeyView>())
  const chosenKeysFor = (fid: string) => new Map(picks.flatMap(p => {
    const v = chosenKeyViews.current.get(`${fid}|${p.contentHash}`)
    return v ? [[p.contentHash, v] as const] : []
  }))
  const [stepIdx, setStepIdx] = useState(0)
  const [allow, setAllow] = useState<Set<string>>(new Set())
  // Steps whose boxes the user has clicked. "Nothing ticked" means "allow all"
  // only on a step they never touched: once they have chosen, an empty tick set
  // (they unticked everything, or their ticked item was answered elsewhere and
  // removed) means deny the rest — never silently allow it.
  const [touched, setTouched] = useState<Set<StepKind>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Latest check wins: a slow response from an older check (or one overtaken by a
  // commit) must not overwrite newer state.
  const checkSeq = useRef(0)
  // A change signalled while the question is open (or mid-commit) is re-read as
  // soon as it closes — see the effect below. Driven by rendered state, not by
  // commit's own control flow: a commit that failed used to leave this pending
  // forever, and every later signal hit the same guard.
  const busyRef = useRef(false)
  busyRef.current = busy
  // The open question as rendered — read by pruneAnsweredElsewhere, which runs
  // from check()'s (folderId-scoped) closure.
  const shownRef = useRef({ triggers, grants, picks, chosen, needsInstall, stepIdx })
  shownRef.current = { triggers, grants, picks, chosen, needsInstall, stepIdx }
  const pendingRecheck = useRef(false)
  // True once children have rendered — from then on the flow overlays them.
  const hasRunRef = useRef(false)
  // Bumped by the folderId effect. `commit()` awaits the server, and on mobile
  // the gate can be handed another app's folderId meanwhile — its results must
  // not land on the new app's state. A generation, not the id: A → B → A is a
  // fresh visit to A, and the old commit's result belongs to neither.
  const folderGen = useRef(0)
  const folderIdRef = useRef(folderId)
  folderIdRef.current = folderId
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])
  const checkRef = useRef<() => Promise<void>>(async () => {})
  // Folders this gate already waits on: every consent signal during a long save
  // re-runs check(), and each would otherwise queue its own re-read.
  const waitingOn = useRef(new Set<string>())

  const check = useCallback(async () => {
    const seq = ++checkSeq.current
    const snapshot = commitSnapshot(folderId)
    // A save for this folder (by this gate or one that was closed) overlapped
    // what we just read, so it may predate the write. Read again once the save
    // has landed — if this gate is still mounted and still on that folder.
    const rereadAfterCommit = () => {
      if (!commitInFlight(folderId)) { void checkRef.current(); return }
      if (waitingOn.current.has(folderId)) return
      waitingOn.current.add(folderId)
      afterCommitSettles(folderId, () => {
        waitingOn.current.delete(folderId)
        if (mountedRef.current && folderIdRef.current === folderId) void checkRef.current()
      })
    }
    try {
      const { access } = await miniappApi.listAccess(folderId)
      const inst = access.some(a => a.isSelf)
      // `null` = we could not read the list (transient failure, or no manage
      // access). NOT the same as "nothing outstanding": clearing on a failed
      // re-check would make a prompt the user has not answered disappear and
      // never come back — the exact silence this gate exists to end.
      let items: MiniAppConsentItem[] | null = null
      try { items = (await miniappApi.getConsentOutstanding(folderId)).outstanding } catch { /* keep what we have */ }
      if (seq !== checkSeq.current) return
      // Mid-flow the question on screen is frozen (see flowOpenRef): applying a
      // new item here would slip it into the same confirm the user is already
      // answering. Re-run once they are done.
      // Frozen against ADDING only. An item answered elsewhere meanwhile
      // (Settings, another tab) must leave: re-asked here, "Deny all" or an
      // unticked box would overwrite that answer.
      if (flowOpenRef.current) {
        pendingRecheck.current = true
        if (items) pruneAnsweredElsewhere(items)
        return
      }
      if (snapshot.stale()) { rereadAfterCommit(); return }
      setNeedsInstall(!inst)
      if (items) {
        // Schedules share the automation step: they run code unattended too, and the
        // server fail-closes them until decided — filtering them out left them inert
        // with no prompt on the first-open path.
        setTriggers(items.filter(i => i.itemType === 'trigger' || i.itemType === 'schedule'))
        // Folders AND keybook keys (0326): both are what the app may reach. A key this person can't open never comes
        // as an "allow" item (the server sends its secrets as picks instead).
        setGrants(items.filter(i => i.itemType === 'grant' || (i.itemType === 'key' && i.key?.canAllow !== false)))
        setPicks(items.filter(i => i.itemType === 'key-pick'))
      }
      // A failed read keeps what is on screen, but must NOT re-open a flow the user
      // already finished: it would re-raise the items they just answered with every
      // checkbox reset, and "Deny all" on that stray prompt overwrites an allow.
      setPhase(prev => (items || prev === 'loading') ? 'flow' : prev)
    } catch {
      // Can't read the manage view (viewer lacks manage — e.g. a read-only
      // audience — or transient). Don't blindly run: check install-state
      // read-only. An UNINSTALLED app shown to someone who can't install it gets a
      // "needs the author/admins to install it" message instead of a degraded
      // (no-elevation) run. Installed → run. Can't determine → fail open (the real
      // boundary is server-side). (#254 review — E-V1 behavior.)
      try {
        const { installed } = await miniappApi.isInstalled(folderId)
        if (seq !== checkSeq.current) return
        // Same freeze as the success path: a failed read here (a network blip, a
        // safeguard 403) must not wipe a question the user is part-way through —
        // that would drop their ticks and let the app run on items nobody
        // decided. Re-read once the question closes.
        if (flowOpenRef.current) { pendingRecheck.current = true; return }
        if (snapshot.stale()) { rereadAfterCommit(); return }
        setPhase(installed ? 'done' : 'needs-install')
      } catch {
        if (seq !== checkSeq.current || flowOpenRef.current) { pendingRecheck.current = true; return }
        if (snapshot.stale()) { rereadAfterCommit(); return }
        setPhase('done')
      }
    }
  }, [folderId])
  checkRef.current = check

  useEffect(() => {
    // Reset the ITEMS too, not just the flow position: a failed outstanding-read
    // now keeps what it has (so a transient failure can't drop a prompt), which
    // means a stale list would otherwise survive a folderId change — on mobile
    // the runner swaps folderId without remounting, so app A's prompt would be
    // shown as app B's, and Confirm would write A's decisions against B.
    setPhase('loading'); setStepIdx(0); setAllow(new Set()); setTouched(new Set()); setError(null)
    setTriggers([]); setGrants([]); setPicks([]); setChosen(new Set()); setNeedsInstall(false)
    // A commit still running belongs to the previous app: its spinner must not
    // lock this app's buttons (it no longer touches state — see commit()).
    setBusy(false)
    folderGen.current++
    hasRunRef.current = false
    void check()
    return () => { checkSeq.current++ }
  }, [check])

  // An open question still re-reads (check() only prunes it); a commit in
  // progress does not — its own result settles the question.
  useMiniappConsentSignal(folderId, () => {
    if (busyRef.current) pendingRecheck.current = true
    else void check()
  })

  const steps = useMemo(() => stepsFor(needsInstall, triggers.length, grants.length, picks.length), [needsInstall, triggers.length, grants.length, picks.length])

  // While the user is working through the flow the list is FROZEN. An item that
  // arrived mid-flow would land on the confirm step unticked and commit as
  // `denied` — a permanent decision on something they were never shown. It is
  // picked up by the re-check that runs once the flow is answered.
  const flowOpen = phase === 'flow' && steps.length > 0
  const ownUi = phase === 'needs-install' || flowOpen
  const onOwnUiChangeRef = useRef(onOwnUiChange)
  onOwnUiChangeRef.current = onOwnUiChange
  useEffect(() => { onOwnUiChangeRef.current?.(ownUi) }, [ownUi])
  const flowOpenRef = useRef(false)
  flowOpenRef.current = flowOpen

  // Drop from the open question whatever the server no longer lists as
  // outstanding — never add. The user stays on the step they were on; if that
  // step emptied, the next one that remains (steps before it were already seen).
  function pruneAnsweredElsewhere(outstanding: MiniAppConsentItem[]) {
    const still = new Set(outstanding.map(i => i.contentHash))
    const shown = shownRef.current
    const t = shown.triggers.filter(i => still.has(i.contentHash))
    const g = shown.grants.filter(i => still.has(i.contentHash))
    // A pick chosen in THIS flow leaves the server's list (it is resolved) but stays on screen, marked done.
    const p = shown.picks.filter(i => still.has(i.contentHash) || shown.chosen.has(i.contentHash))
    if (t.length === shown.triggers.length && g.length === shown.grants.length && p.length === shown.picks.length) return
    const before = stepsFor(shown.needsInstall, shown.triggers.length, shown.grants.length, shown.picks.length)
    const after = stepsFor(shown.needsInstall, t.length, g.length, p.length)
    const cur = before[Math.min(shown.stepIdx, before.length - 1)]
    setTriggers(t); setGrants(g); setPicks(p)
    setAllow(prev => new Set([...prev].filter(h => still.has(h))))
    // Nothing left to ask: no steps means `flowOpen` is false, so the app shows
    // and the pending re-read runs.
    if (!after.length) return
    const at = after.findIndex(s => STEP_ORDER.indexOf(s) >= STEP_ORDER.indexOf(cur))
    setStepIdx(at === -1 ? after.length - 1 : at)
  }

  // Re-read the moment the question closes (or a commit settles): whatever
  // arrived while it was open is then raised as its own prompt. Keyed on
  // rendered state so no control path can leave it pending forever.
  useEffect(() => {
    if (flowOpen || busy || !pendingRecheck.current) return
    pendingRecheck.current = false
    void check()
  }, [flowOpen, busy, check])

  // Before the early returns below, so it reports every phase, and it must
  // stay above them — a hook after a conditional return is a different hook
  // on the next render.
  const appMounted = phase !== 'loading' && phase !== 'needs-install'
  useEffect(() => {
    onAppMountedChange?.(appMounted)
    // Unmounting the gate takes the app with it.
    return () => onAppMountedChange?.(false)
  }, [appMounted, onAppMountedChange])

  if (phase === 'loading') {
    if (loadingView) return <>{loadingView}</>
    return <div className="flex-1 flex items-center justify-center themed-text-muted text-sm"><Loader2 size={16} className="animate-spin mr-2" />Loading…</div>
  }
  if (phase === 'needs-install') {
    // Read-only audience, app not installed → tell them, don't run it degraded.
    // The app unmounts here, so a later flow must not assume it is still running.
    hasRunRef.current = false
    return (
      <div className="flex-1 flex items-center justify-center p-8 themed-bg-primary">
        <div className="w-full max-w-md text-center">
          <ShieldCheck size={30} className="mx-auto themed-icon mb-3" />
          <h2 className="text-base font-semibold themed-text mb-1.5">“{folderName ?? 'This PDApp'}” hasn’t been approved to run yet</h2>
          <p className="text-sm themed-text-secondary">Its author or an admin needs to give it permission before it can run.</p>
        </div>
      </div>
    )
  }

  // One stable wrapper around the running app, so opening the overlay later is a
  // sibling insert — React keeps the app mounted.
  const running = (overlay: ReactNode) => (
    <div className="relative flex-1 flex flex-col min-h-0">
      {children}
      {/* A partial failure closes the question so the re-read can rebuild it —
          which means the message about what didn't save would flash past with
          nothing left to render it. It rides above the running app instead. */}
      {error && !overlay && (
        <div role="alert" className="absolute top-2 left-1/2 -translate-x-1/2 z-[120] max-w-[90%] flex items-center gap-2 pl-3 pr-1 py-1 rounded border border-red-500/40 bg-red-500/15 text-xs text-red-300">
          <span className="min-w-0">{error}</span>
          {/* Nothing else clears it once the question has closed (the re-read
              may find the item already resolved), so it must be dismissable. */}
          <button onClick={() => setError(null)} aria-label="Dismiss" className="shrink-0 p-0.5 rounded hover:bg-red-500/20"><X size={12} /></button>
        </div>
      )}
      {overlay}
    </div>
  )
  if (phase === 'done' || steps.length === 0) {
    hasRunRef.current = true
    return running(null)
  }

  const total = steps.length
  const idx = Math.min(stepIdx, total - 1)
  const step = steps[idx]
  const isLast = idx === total - 1
  const stepItems = step === 'triggers' ? triggers : step === 'grants' ? grants : []
  const checked = stepItems.filter(i => allow.has(i.contentHash)).length
  const allowAllShortcut = checked === 0 && !(step && touched.has(step))

  const toggle = (hash: string) => {
    if (step && !touched.has(step)) setTouched(prev => new Set(prev).add(step))
    toggleAllow(hash)
  }
  const toggleAllow = (hash: string) => setAllow(prev => {
    const next = new Set(prev)
    if (next.has(hash)) next.delete(hash); else next.add(hash)
    return next
  })
  const setStepAll = (on: boolean) => setAllow(prev => {
    const next = new Set(prev)
    for (const i of stepItems) { if (on) next.add(i.contentHash); else next.delete(i.contentHash) }
    return next
  })

  // Commit everything once: install (if needed) + a decision per item (checked =
  // allow, else deny). Reversible later in Settings.
  const commit = async () => {
    setBusy(true); setError(null)
    // Invalidate any in-flight check — it would re-show items decided here.
    checkSeq.current++
    // Every await below can outlive this folder (mobile swaps folderId without a
    // remount). Results for app A must never clear or open app B's question.
    const forFolder = folderId
    const gen = folderGen.current
    const moved = () => folderGen.current !== gen
    const endCommit = beginCommit(forFolder)
    // #1316 review round 2 (owner, 2026-10-28): the Confirm the person pressed FINISHES as the identity that pressed it —
    // install and decisions go as it, never as the next session; nothing is shown once that session ended (the app
    // window remounts then)
    const batch = pinnedBatch()
    const gone = () => moved() || batch.ended()
    try {
      if (needsInstall) await batch.run(() => miniappApi.install(folderId))
      if (gone()) return
      // A pick left unchosen is set aside (`denied`), like an unticked item; one chosen is already saved.
      const items = [...triggers, ...grants, ...picks.filter(i => !chosen.has(i.contentHash))]
      if (items.length) {
        // ONE call: a decision per item meant one permission event each, and the
        // event drops the running app's socket — N reconnects for one confirm.
        const { results } = await miniappApi.decideConsentBatch(folderId, items.map(item => ({
          itemType: item.itemType, contentHash: item.contentHash, label: item.label,
          targetFolderId: item.targetFolderId, decision: item.itemType !== 'key-pick' && allow.has(item.contentHash) ? 'allowed' : 'denied',
        })), batch.opts())
        if (gone()) return
        const failed = results.filter(r => !r.ok)
        if (failed.length) {
          // Partial by design: the rest were saved. Say what didn't stick, drop
          // the now-stale question, and let the re-read effect rebuild it with
          // only what is still outstanding.
          setError(failed[0]?.error ?? `${failed.length} item(s) could not be saved`)
          // `needsInstall` too: install runs before the decisions, so by now it
          // is done. Leaving it set keeps an `install` step in `steps`, which
          // keeps the flow open — blocking the very re-read this schedules, and
          // asking the user to install an app that just installed.
          setTriggers([]); setGrants([]); setPicks([]); setChosen(new Set()); setAllow(new Set()); setTouched(new Set()); setStepIdx(0); setNeedsInstall(false)
          pendingRecheck.current = true
          return
        }
      }
      // Drop the decided items too: a later re-check that cannot read the list
      // would otherwise still be holding them, ready to re-raise answered items.
      setPhase('done'); setStepIdx(0); setAllow(new Set()); setTouched(new Set()); setTriggers([]); setGrants([]); setPicks([]); setChosen(new Set())
    } catch (e) {
      if (gone()) return
      // The flow stays open with its items so the user can retry Confirm; the
      // re-read effect picks up anything new once it closes. #1316: a dropped answer shows no error.
      const text = errorText(e)
      if (text !== null) setError(text)
      // …but a retry must not re-send what already stuck. A throw can follow a
      // PARTIAL write (the server announces it, while we were busy and ignoring
      // signals), or a client timeout after a full one; items answered in
      // another tab meanwhile are just as stale. Re-read now: while the question
      // is open that only removes what is no longer outstanding.
      void check()
    }
    finally {
      if (!moved()) setBusy(false)
      // After setBusy: a gate that deferred its read (A → B → A, or a window
      // closed and reopened mid-save) re-reads now that the write has landed.
      endCommit()
    }
  }
  const advance = () => { if (isLast) void commit(); else setStepIdx(idx + 1) }
  const back = () => setStepIdx(i => Math.max(0, i - 1))

  const flow = (
    <div className="flex-1 flex items-center justify-center p-8 themed-bg-primary">
      <div className="w-full max-w-md">
        {step === 'install' ? (
          <div className="text-center">
            <ShieldCheck size={30} className="mx-auto themed-icon mb-3" />
            <h2 className="text-base font-semibold themed-text mb-1.5">“{folderName ?? 'This PDApp'}” needs your permission to run</h2>
            <p className="text-sm themed-text-secondary mb-5">It will be able to read and write files in its own folder.</p>
            <StepLabel idx={idx} total={total} />
            <button onClick={advance} disabled={busy} className="inline-flex items-center gap-1.5 text-sm themed-btn-primary px-4 py-2 rounded disabled:opacity-40">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} {isLast ? 'Allow' : 'Next'}
            </button>
          </div>
        ) : step === 'confirm' ? (
          <div>
            <div className="text-center mb-4">
              <ShieldCheck size={26} className="mx-auto themed-icon mb-2" />
              <h2 className="text-base font-semibold themed-text">Review &amp; confirm</h2>
              <p className="text-xs themed-text-muted mt-1">Nothing runs until you confirm. You can change any of this later in Settings.</p>
            </div>
            <ul className="space-y-1 mb-4 max-h-[40vh] overflow-y-auto">
              {needsInstall && (
                <li className="flex items-center gap-2 px-3 py-2 rounded themed-bg-secondary">
                  <Check size={14} className="text-emerald-400 shrink-0" />
                  <span className="text-sm themed-text flex-1">Its own folder — read &amp; write</span>
                </li>
              )}
              {[...triggers, ...grants].map(item => {
                const on = allow.has(item.contentHash)
                return (
                  <li key={item.contentHash} className="flex items-center gap-2 px-3 py-2 rounded themed-bg-secondary">
                    {on ? <Check size={14} className="text-emerald-400 shrink-0" /> : <X size={14} className="themed-text-muted shrink-0" />}
                    <ConsentItemLabel itemType={item.itemType} label={item.label} schedule={item.schedule} textClassName={on ? 'themed-text' : 'themed-text-muted line-through'} />
                    <span className={`text-[10px] uppercase tracking-wide shrink-0 ${on ? 'text-emerald-400' : 'themed-text-muted'}`}>{on ? 'allow' : 'deny'}</span>
                  </li>
                )
              })}
              {picks.map(item => (
                <AppKeyPickSummary
                  key={item.contentHash}
                  name={item.pick?.name ?? item.label}
                  done={chosen.has(item.contentHash)}
                  chosenKey={chosenKeyViews.current.get(`${folderId}|${item.contentHash}`)}
                />
              ))}
            </ul>
            <StepLabel idx={idx} total={total} />
            <div className="flex items-center justify-center gap-2">
              <button onClick={advance} disabled={busy} className="inline-flex items-center gap-1.5 text-sm themed-btn-primary px-5 py-2 rounded disabled:opacity-40">
                {busy && <Loader2 size={15} className="animate-spin" />} Confirm
              </button>
            </div>
            {idx > 0 && (
              <div className="text-center mt-3">
                <button onClick={back} disabled={busy} className="inline-flex items-center gap-1 text-xs themed-text-muted hover:themed-text disabled:opacity-40"><ChevronLeft size={14} /> Back</button>
              </div>
            )}
          </div>
        ) : step === 'keys' ? (
          <div>
            <AppKeysStepIntro total={picks.length} chosen={picks.filter(i => chosen.has(i.contentHash)).length} />
            <div className="mb-4 max-h-[50vh] overflow-y-auto">
              <AppKeyPicks
                folderId={folderId}
                items={picks.map(i => ({ contentHash: i.contentHash, name: i.pick?.name ?? i.label, description: i.pick?.description ?? null, needs: i.pick?.needs ?? 'no-key' }))}
                chosen={chosen}
                onChosen={(gen => (h: string) => { if (folderGen.current === gen) setChosen(prev => new Set(prev).add(h)) })(folderGen.current)}
                onBusy={(forFolder => (b: boolean) => { pickSaveDelta(forFolder, b ? 1 : -1); if (mountedRef.current) setPickSaveTick(t => t + 1) })(folderId)}
                disabled={busy || pickSaving > 0}
                chosenKeys={chosenKeysFor(folderId)}
                onUse={(forFolder => (h: string, view: ChosenKeyView) => { chosenKeyViews.current.set(`${forFolder}|${h}`, view) })(folderId)}
              />
            </div>
            <StepLabel idx={idx} total={total} />
            <div className="flex items-center justify-center gap-2">
              <button onClick={advance} disabled={busy || pickSaving > 0} className="inline-flex items-center gap-1.5 text-sm themed-btn-primary px-4 py-2 rounded disabled:opacity-40" data-testid="pdapp-setup-keys-continue">
                {busy && <Loader2 size={15} className="animate-spin" />}{isLast ? 'Confirm' : 'Continue'}
              </button>
            </div>
            {idx > 0 && (
              <div className="text-center mt-3">
                <button onClick={back} disabled={busy || pickSaving > 0} className="inline-flex items-center gap-1 text-xs themed-text-muted hover:themed-text disabled:opacity-40"><ChevronLeft size={14} /> Back</button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="text-center mb-4">
              {step === 'triggers' ? <Zap size={26} className="mx-auto themed-icon mb-2" /> : <FolderPlus size={26} className="mx-auto themed-icon mb-2" />}
              <h2 className="text-base font-semibold themed-text">{step === 'triggers' ? 'Allow this app to run automatically?' : stepItems.some(i => i.itemType === 'key') ? 'Allow this app to reach these folders and keys?' : 'Allow this app to reach these folders?'}</h2>
              <p className="text-xs themed-text-muted mt-1">
                {step === 'triggers' ? 'It will run its own code when a checked event happens or on its schedule.' : stepItems.some(i => i.itemType === 'key') ? 'Read access to each checked folder (tune it later in Settings); each checked key is read by the app’s code when it runs.' : 'Read access to each checked folder (tune it later in Settings).'}
              </p>
            </div>
            <ul className="space-y-1 mb-4 max-h-[40vh] overflow-y-auto">
              {stepItems.map(item => {
                const on = allow.has(item.contentHash)
                return (
                  <li key={item.contentHash}>
                    <button onClick={() => toggle(item.contentHash)} className={`w-full flex items-center gap-2 px-3 py-2 rounded text-left border transition-colors ${on ? 'bg-emerald-500/10 border-emerald-500/40' : 'themed-bg-secondary themed-border-secondary'}`}>
                      <span className={`shrink-0 w-4 h-4 rounded-[4px] border flex items-center justify-center ${on ? 'bg-emerald-500 border-emerald-500' : 'themed-border'}`}>
                        {on && <Check size={12} className="text-white" />}
                      </span>
                      <ConsentItemLabel itemType={item.itemType} label={item.label} schedule={item.schedule} />
                    </button>
                  </li>
                )
              })}
            </ul>
            <StepLabel idx={idx} total={total} />
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => { setStepAll(false); advance() }} disabled={busy} className="text-sm px-3 py-2 rounded themed-text-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40">Deny all</button>
              <button onClick={() => { if (allowAllShortcut) setStepAll(true); advance() }} disabled={busy} className="inline-flex items-center gap-1.5 text-sm themed-btn-primary px-4 py-2 rounded disabled:opacity-40">
                {busy && <Loader2 size={15} className="animate-spin" />}
                {allowAllShortcut ? 'Allow all' : checked === 0 ? 'Continue' : `Allow ${checked}/${stepItems.length}`}
              </button>
            </div>
            {idx > 0 && (
              <div className="text-center mt-3">
                <button onClick={back} disabled={busy} className="inline-flex items-center gap-1 text-xs themed-text-muted hover:themed-text disabled:opacity-40"><ChevronLeft size={14} /> Back</button>
              </div>
            )}
          </div>
        )}
        {error && <div className="text-xs text-red-400 mt-3 text-center">{error}</div>}
      </div>
    </div>
  )

  if (!hasRunRef.current) return flow
  return running(
    // Above the app's own fullscreen layer: HtmlEditor renders fullscreen as
    // `fixed inset-0 z-[100]`, so at z-10 the prompt was painted underneath a
    // fullscreened app — invisible exactly when the app fills the screen. The
    // wrapper deliberately does NOT open a stacking context (no `isolate`): that
    // would trap the app's fullscreen layer inside this box, and the shell chrome
    // (share bar z-50, mobile header z-[60]) would paint over a fullscreened app.
    // Not `aria-modal`: this covers the app's pane, not the page — a fullscreened
    // app (which portals to the body) stays visible and usable around it, and the
    // pending badge is the signal there. Claiming modality would be a lie to a
    // screen reader.
    <div className="absolute inset-0 z-[110] flex flex-col overflow-y-auto" role="dialog" aria-label="Permissions">
      {flow}
    </div>,
  )
}

function StepLabel({ idx, total }: { idx: number; total: number }) {
  return (
    <div className="text-[11px] font-medium uppercase tracking-wide themed-text-muted mb-2 text-center">
      Permissions · Step {idx + 1} of {total}
    </div>
  )
}
